// Componentes da aba Tendências & Causa-Raiz, com dado real por prop; SimChip marca valor estimado.
import { alpha, getContrastRatio } from "@mui/material/styles";
import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Layer,
  Line,
  LineChart,
  ReferenceLine,
  Rectangle,
  ResponsiveContainer,
  Sankey,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CHART_MARGIN,
  chartAxisProps,
  chartGridProps,
  HeatCell,
  formatInt,
  formatPct,
  heatHeadSx,
  heatRowLabelSx,
  pctColor,
  rampaCalor,
  semaforoCores,
  trHover,
} from "./shared";

export const SimChip = ({ theme, label = "dados simulados" }) => (
  <Chip
    size="small"
    label={label}
    sx={{
      height: 22,
      fontSize: 11,
      fontWeight: 700,
      color: theme.palette.text.primary,
      backgroundColor: alpha(semaforoCores(theme).atencao, 0.2),
    }}
  />
);

/* ----------------------- curva de reincidência por dia ----------------------- */
export function CurvaReincidencia({ styles, theme, data = [] }) {
  // Série única em cor primária; vermelho fica reservado ao semáforo.
  const cor = theme.palette.primary.main;
  return (
    <Box sx={styles.chartBox}>
      {/* Topo maior para os rótulos 30d/60d/90d não serem cortados. */}
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ ...CHART_MARGIN, top: 20, right: 12 }}>
          <CartesianGrid {...chartGridProps(theme)} />
          <XAxis
            dataKey="dia"
            ticks={[1, 15, 30, 45, 60, 75, 90]}
            tickFormatter={(v) => `${v}d`}
            {...chartAxisProps(theme)}
          />
          <YAxis tickFormatter={(v) => `${v}%`} {...chartAxisProps(theme)} />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            itemStyle={styles.chartTooltipItem}
            labelStyle={styles.chartTooltipLabel}
            formatter={(value) => [`${value}%`, "Acumulado"]}
            labelFormatter={(l) => `Dia ${l}`}
          />
          {[30, 60, 90].map((d) => (
            <ReferenceLine
              key={d}
              x={d}
              stroke={alpha(theme.palette.text.primary, 0.25)}
              strokeDasharray="4 4"
              label={{
                value: `${d}d`,
                position: "top",
                fontSize: 10,
                fill: theme.palette.text.secondary,
              }}
            />
          ))}
          <Area
            type="monotone"
            dataKey="acumulado"
            stroke={cor}
            strokeWidth={2}
            fill={cor}
            fillOpacity={0.1}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
}

/* ----------------------- carta de controle (SPC) ----------------------- */
export function CartaControle({ styles, theme, data = [] }) {
  const forced = data.filter((s) => s.foraControle);
  const sem = semaforoCores(theme);
  // Rótulo das linhas de referência em texto neutro; a cor fica só na linha.
  const rotulo = (value) => ({
    value,
    position: "right",
    fontSize: 10,
    fill: theme.palette.text.secondary,
  });
  return (
    <Box sx={styles.chartBox}>
      {/* extendDomain nas linhas põe UCL/LCL dentro da escala; direita larga para os rótulos. */}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ ...CHART_MARGIN, right: 44 }}>
          <CartesianGrid {...chartGridProps(theme)} />
          <XAxis dataKey="semana" {...chartAxisProps(theme)} />
          <YAxis
            tickFormatter={(v) => `${v}%`}
            domain={[
              (min) => Math.max(0, Math.floor(min - 2)),
              (max) => Math.ceil(max + 2),
            ]}
            allowDecimals={false}
            {...chartAxisProps(theme)}
          />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            itemStyle={styles.chartTooltipItem}
            labelStyle={styles.chartTooltipLabel}
            formatter={(value, name) => [`${value}%`, name]}
          />
          <ReferenceLine
            y={data[0]?.ucl}
            stroke={sem.critico}
            strokeDasharray="5 5"
            ifOverflow="extendDomain"
            label={rotulo("UCL")}
          />
          <ReferenceLine
            y={data[0]?.media}
            stroke={alpha(theme.palette.text.primary, 0.4)}
            ifOverflow="extendDomain"
            label={rotulo("Média")}
          />
          <ReferenceLine
            y={data[0]?.lcl}
            stroke={sem.ok}
            strokeDasharray="5 5"
            ifOverflow="extendDomain"
            label={rotulo("LCL")}
          />
          <Line
            type="monotone"
            dataKey="pct"
            name="Reincidência"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        </LineChart>
      </ResponsiveContainer>
      {forced.length > 0 && (
        <Typography sx={{ ...styles.cardSubtitle, mt: 0.5 }}>
          ⚠ {forced.map((f) => f.semana).join(", ")} fora de controle (acima da UCL)
        </Typography>
      )}
    </Box>
  );
}

/* ----------------------- Sankey Abertura → Fechamento ----------------------- */
// Paleta categórica do tenant sem as cores do semáforo (status não é cor de série).
const sankeyPalette = (theme) => {
  const status = Object.values(semaforoCores(theme)).map((c) => String(c).toLowerCase());
  return [
    theme.palette.primary.main,
    ...(theme.palette.chartPallet || []).filter((c) => !status.includes(String(c).toLowerCase())),
  ];
};

const sankeyNodeColor = (theme, index) => {
  const palette = sankeyPalette(theme);
  return palette[index % palette.length];
};

// fora: rótulos nas bordas externas (fora das faixas) e nome longo abreviado; o nome inteiro vai no <title>.
function SankeyNode({ x, y, width, height, index, payload, theme, fora = false }) {
  const isLeft = payload.depth === 0;
  const color = sankeyNodeColor(theme, index);
  const rotulo = fora && payload.name.length > 34 ? `${payload.name.slice(0, 34)}…` : payload.name;
  const rotuloNaEsquerda = fora ? isLeft : !isLeft;
  return (
    <Layer>
      <Rectangle
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        fillOpacity={0.95}
        radius={2}
      />
      <text
        x={rotuloNaEsquerda ? x - 6 : x + width + 6}
        y={y + height / 2}
        textAnchor={rotuloNaEsquerda ? "end" : "start"}
        dominantBaseline="middle"
        fontSize={11}
        fill={theme.palette.text.primary}
      >
        <title>{payload.name}</title>
        {rotulo}
      </text>
    </Layer>
  );
}

// Link na cor do nó de origem; o índice vem do link original em data, porque o recharts troca source pelo objeto do nó.
function SankeyLink({
  sourceX,
  sourceY,
  sourceControlX,
  targetX,
  targetY,
  targetControlX,
  linkWidth,
  index,
  data,
  theme,
}) {
  const color = sankeyNodeColor(theme, data?.links?.[index]?.source ?? 0);
  return (
    <Layer>
      <path
        d={`
          M${sourceX},${sourceY}
          C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}
        `}
        fill="none"
        stroke={color}
        strokeWidth={linkWidth}
        strokeOpacity={0.4}
      />
    </Layer>
  );
}

export function SankeyMotivos({
  styles,
  theme,
  data = { nodes: [], links: [] },
  nodePadding = 26,
  unidade = "chamados",
  altura = 360,
  rotulosFora = false,
}) {
  return (
    <Box sx={{ ...styles.chartBox, height: altura }}>
      {/* O recharts já nomeia o link como "origem - destino"; o formatter só formata o valor. */}
      <ResponsiveContainer width="100%" height="100%">
        <Sankey
          data={data}
          nodePadding={nodePadding}
          nodeWidth={12}
          margin={{ left: rotulosFora ? 210 : 8, right: rotulosFora ? 210 : 8, top: 12, bottom: 12 }}
          link={<SankeyLink theme={theme} data={data} />}
          node={<SankeyNode theme={theme} fora={rotulosFora} />}
        >
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            itemStyle={styles.chartTooltipItem}
            labelStyle={styles.chartTooltipLabel}
            formatter={(value, name) => [`${formatInt(value)} ${unidade}`, name]}
          />
        </Sankey>
      </ResponsiveContainer>
    </Box>
  );
}

/* ----------------------- Matriz tipo de OS × motivo (taxa de retorno) ----------------------- */
const AMOSTRA_MINIMA = 20;

// Células pelo semáforo (mesma linguagem do heatmap do Resumo); amostra pequena aparece atenuada.
export function MatrizTaxa({ theme, data = { tipos: [], motivos: [], cells: [] } }) {
  const { tipos, motivos, cells } = data;
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `minmax(190px, 1.4fr) repeat(${motivos.length}, minmax(92px, 1fr))`,
          gap: "2px",
          minWidth: 190 + motivos.length * 92,
        }}
      >
        <Box />
        {motivos.map((m) => (
          <Box key={m} sx={{ ...heatHeadSx(theme), alignSelf: "end" }}>
            {m}
          </Box>
        ))}
        {tipos.map((tipo, i) => (
          <Box key={tipo} sx={{ display: "contents" }}>
            <Box sx={{ ...heatRowLabelSx(theme), display: "flex", alignItems: "center" }}>{tipo}</Box>
            {cells[i].map((c, j) => {
              if (!c.total) {
                return <HeatCell key={j} theme={theme} valor="—" color={theme.palette.text.secondary} />;
              }
              const pequena = c.total < AMOSTRA_MINIMA;
              return (
                <HeatCell
                  key={j}
                  theme={theme}
                  bg={alpha(pctColor(theme, c.pct), 0.18)}
                  opacity={pequena ? 0.5 : 1}
                  valor={formatPct(c.pct)}
                  apoio={`${formatInt(c.reinc)}/${formatInt(c.total)}`}
                  title={`${tipo} • ${motivos[j]}: ${formatInt(c.reinc)} de ${formatInt(c.total)} OS voltaram${
                    pequena ? ` (amostra pequena, menos de ${AMOSTRA_MINIMA} OS)` : ""
                  }`}
                />
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ----------------------- Heatmap Cidade × Motivo ----------------------- */
// Texto branco ou preto, o que tiver mais contraste com o fundo da célula.
const textoSobre = (theme, bg) => {
  const { white, black } = theme.palette.common;
  return getContrastRatio(bg, white) >= getContrastRatio(bg, black) ? white : black;
};

// Legenda da escala (menos → mais chamados); vai no topRight do card, como a SemaforoLegend do Resumo.
export function HeatmapEscala({ theme }) {
  const rampa = rampaCalor(theme);
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>menos</Typography>
      {rampa.map((c, i) => (
        <Box key={i} sx={{ width: 16, height: 10, borderRadius: "2px", backgroundColor: c }} />
      ))}
      <Typography sx={{ fontSize: 11, color: theme.palette.text.secondary }}>mais chamados</Typography>
    </Stack>
  );
}

export function Heatmap({ theme, data = { cidades: [], motivos: [], matrix: [] } }) {
  const { cidades: cs, motivos, matrix } = data;
  const max = matrix.length ? Math.max(...matrix.flat()) : 0;
  const rampa = rampaCalor(theme);
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `minmax(150px, 1.4fr) repeat(${motivos.length}, 1fr)`,
          gap: "2px",
          minWidth: 520,
        }}
      >
        <Box />
        {motivos.map((m) => (
          <Box key={m} sx={{ ...heatHeadSx(theme), alignSelf: "end" }}>
            {m}
          </Box>
        ))}
        {cs.map((cidade, i) => (
          <Box key={cidade} sx={{ display: "contents" }}>
            <Box sx={{ ...heatRowLabelSx(theme), display: "flex", alignItems: "center" }}>{cidade}</Box>
            {matrix[i].map((v, j) => {
              const ratio = max ? v / max : 0;
              // Zero fica neutro; o resto cai em um dos degraus da rampa.
              const bg = v > 0 ? rampa[Math.min(rampa.length - 1, Math.floor(ratio * rampa.length))] : undefined;
              return (
                <HeatCell
                  key={j}
                  theme={theme}
                  bg={bg}
                  color={v > 0 ? textoSobre(theme, bg) : theme.palette.text.secondary}
                  valor={formatInt(v)}
                  title={`${cidade} • ${motivos[j]}: ${formatInt(v)}`}
                />
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/* ----------------------- multi-reincidência / crônicos ----------------------- */
// Crônico = mesma instalação com 3 retornos ou mais (mesma regra do KPI da aba).
const faixaCronica = (faixa) => ["3x", "4x", "5x+"].includes(faixa);

export function CronicosDistribuicao({ styles, theme, data = [] }) {
  const sem = semaforoCores(theme);
  return (
    <Box sx={styles.chartBox}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={CHART_MARGIN}>
          <CartesianGrid {...chartGridProps(theme)} />
          <XAxis dataKey="faixa" {...chartAxisProps(theme)} />
          <YAxis {...chartAxisProps(theme)} />
          <RechartsTooltip
            contentStyle={styles.chartTooltip}
            itemStyle={styles.chartTooltipItem}
            labelStyle={styles.chartTooltipLabel}
            cursor={{ fill: alpha(theme.palette.text.primary, 0.04) }}
            formatter={(value) => [formatInt(value), "Instalações"]}
          />
          <Bar dataKey="clientes" radius={[2, 2, 0, 0]} maxBarSize={24}>
            {data.map((d, i) => (
              <Cell
                key={i}
                fill={faixaCronica(d.faixa) ? sem.critico : theme.palette.primary.main}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

export function CronicosTabela({ styles, theme, data = [] }) {
  const sem = semaforoCores(theme);
  const cellHead = {
    textAlign: "left",
    padding: "8px 10px",
    position: "sticky",
    top: 0,
    backgroundColor: theme.palette.background.paper,
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.12)}`,
  };
  const cellBody = (idx) => ({
    padding: "8px 10px",
    fontSize: 13,
    borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.06)}`,
    backgroundColor: idx % 2 === 1 ? alpha(theme.palette.text.primary, 0.02) : "transparent",
  });
  return (
    // Mesma altura do gráfico ao lado (chartBox); a lista rola e o cabeçalho fica fixo.
    <Box sx={{ ...styles.chartBox, overflow: "auto" }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={cellHead}>Cliente</Box>
            <Box component="th" sx={cellHead}>Serviço</Box>
            <Box component="th" sx={cellHead}>Cidade</Box>
            <Box component="th" sx={{ ...cellHead, textAlign: "right" }}>Retornos</Box>
          </Box>
        </Box>
        <Box component="tbody">
          {data.map((c, idx) => {
            // Tinta crítica só para crônico (3x+); 2x fica neutro.
            const cronico = Number(c.retornos) >= 3;
            return (
              <Box component="tr" key={c.idClienteServico} sx={trHover(theme)}>
                <Box component="td" sx={{ ...cellBody(idx), fontWeight: 700 }}>
                  {/* Nome longo corta com reticências; o completo e o serviço ficam no tooltip. */}
                  <Tooltip title={`${c.cliente} • serviço ${c.idClienteServico}`} arrow placement="top">
                    <Box
                      component="span"
                      sx={{ display: "block", maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {c.cliente}
                    </Box>
                  </Tooltip>
                </Box>
                <Box component="td" sx={{ ...cellBody(idx), color: theme.palette.text.secondary }}>
                  {c.idClienteServico}
                </Box>
                <Box component="td" sx={cellBody(idx)}>{c.cidade}</Box>
                <Box component="td" sx={{ ...cellBody(idx), textAlign: "right" }}>
                  <Chip
                    size="small"
                    label={`${c.retornos}x`}
                    sx={{
                      height: 22,
                      fontWeight: 800,
                      color: theme.palette.text.primary,
                      backgroundColor: cronico
                        ? alpha(sem.critico, 0.2)
                        : alpha(theme.palette.text.primary, 0.08),
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
